/**
 * Shared email validation utilities.
 *
 * This is the single source of truth for client-side email-format checks
 * across the app. Previously the same regex was duplicated in several places
 * (PolicyEmailCompose, email sequences, customer forms, MPOI, onboarding).
 *
 * The server is always the final authority; this is only a lightweight
 * format gate to catch obvious typos before submitting.
 */

/** Basic client-side email format regex. */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Returns true when the given string looks like a valid email address.
 * Leading/trailing whitespace is ignored.
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email) {
    return false;
  }
  return EMAIL_REGEX.test(email.trim());
};

/**
 * Returns a form error message when the value is a non-empty invalid email,
 * otherwise returns undefined. Use inside Formik `validate` for OPTIONAL
 * email fields (an empty value is considered valid here).
 */
export const validateEmailField = (
  email: string | null | undefined,
  message = "Please enter a valid email address.",
): string | undefined => {
  if (email && email.trim().length > 0 && !isValidEmail(email)) {
    return message;
  }
  return undefined;
};

/**
 * Returns a form error message for a REQUIRED email field: empty values and
 * malformed values both produce an error.
 */
export const validateRequiredEmailField = (
  email: string | null | undefined,
  requiredMessage = "Email is required.",
  invalidMessage = "Please enter a valid email address.",
): string | undefined => {
  if (!email || email.trim().length === 0) {
    return requiredMessage;
  }
  if (!isValidEmail(email)) {
    return invalidMessage;
  }
  return undefined;
};

/**
 * Normalizes a single email value for transport to the API: trims whitespace
 * and lowercases it. Email local-parts are technically case-sensitive per RFC,
 * but in practice all mailbox providers treat them case-insensitively, and the
 * backend expects a canonical lowercase form. Non-string/empty values pass
 * through unchanged (returns "" for empty-ish input).
 */
export const normalizeEmail = (email: string | null | undefined): string => {
  if (!email) {
    return "";
  }
  return email.trim().toLowerCase();
};

/**
 * Normalizes a list of email addresses: trims + lowercases each entry and
 * drops any that are empty after trimming. Useful for to/cc/bcc arrays before
 * sending to the API.
 */
export const normalizeEmailList = (
  emails: Array<string | null | undefined> | null | undefined,
): string[] => {
  if (!emails) {
    return [];
  }
  return emails
    .map((email) => normalizeEmail(email))
    .filter((email) => email.length > 0);
};
